import React, { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, Expand, Trash2 } from "lucide-react";
import { VENDOR_FIELDS } from "@/lib/fields";
import { cellStatus, normalizeValue, rowIssues } from "@/lib/validation";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const cellDomId = (r, c) => `vde-cell-${r}-${c}`;
const LAST = VENDOR_FIELDS.length - 1;

export const VendorTable = ({ rows, onCellChange, onDeleteRow }) => {
  const [editing, setEditing] = useState(null); // { r, c, rid, key, value }
  const [expand, setExpand] = useState(null); // { rid, key, label, value }
  const editorRef = useRef(null);

  const focusCell = useCallback((r, c) => {
    const el = document.getElementById(cellDomId(r, c));
    if (el) el.focus();
  }, []);

  useEffect(() => {
    if (editing && editorRef.current) {
      const el = editorRef.current;
      el.focus();
      try {
        const len = el.value.length;
        el.setSelectionRange(len, len);
      } catch (e) {
        /* noop */
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing?.rid, editing?.key]);

  const startEdit = (r, c, seed) => {
    const row = rows[r];
    const field = VENDOR_FIELDS[c];
    if (!row || !field) return;
    setEditing({
      r,
      c,
      rid: row._rid,
      key: field.key,
      value: seed !== undefined ? seed : row[field.key] ?? "",
    });
  };

  const commit = (move) => {
    if (!editing) return;
    const { r, c, rid, key, value } = editing;
    onCellChange(rid, key, value);
    setEditing(null);
    window.setTimeout(() => {
      if (move === "down") focusCell(Math.min(r + 1, rows.length - 1), c);
      else if (move === "right") {
        if (c < LAST) focusCell(r, c + 1);
        else focusCell(Math.min(r + 1, rows.length - 1), 0);
      } else focusCell(r, c);
    }, 0);
  };

  const cancel = () => {
    if (!editing) return;
    const { r, c } = editing;
    setEditing(null);
    window.setTimeout(() => focusCell(r, c), 0);
  };

  const onCellKeyDown = (e, r, c) => {
    const k = e.key;
    if (k === "Enter" || k === "F2") {
      e.preventDefault();
      startEdit(r, c);
      return;
    }
    if (k === "ArrowRight") {
      e.preventDefault();
      focusCell(r, Math.min(c + 1, LAST));
      return;
    }
    if (k === "ArrowLeft") {
      e.preventDefault();
      focusCell(r, Math.max(c - 1, 0));
      return;
    }
    if (k === "ArrowDown") {
      e.preventDefault();
      focusCell(Math.min(r + 1, rows.length - 1), c);
      return;
    }
    if (k === "ArrowUp") {
      e.preventDefault();
      focusCell(Math.max(r - 1, 0), c);
      return;
    }
    if ((e.metaKey || e.ctrlKey) && k.toLowerCase() === "c") {
      const val = rows[r]?.[VENDOR_FIELDS[c].key] || "";
      if (navigator.clipboard) navigator.clipboard.writeText(val).catch(() => {});
      return;
    }
    if (k === "Delete" || k === "Backspace") {
      e.preventDefault();
      onCellChange(rows[r]._rid, VENDOR_FIELDS[c].key, "");
      return;
    }
    if (!e.metaKey && !e.ctrlKey && !e.altKey && k.length === 1) {
      e.preventDefault();
      startEdit(r, c, normalizeValue(VENDOR_FIELDS[c].key, k));
    }
  };

  const onEditorKeyDown = (e, field) => {
    if (e.key === "Escape") {
      e.preventDefault();
      cancel();
      return;
    }
    if (e.key === "Tab") {
      e.preventDefault();
      commit("right");
      return;
    }
    if (e.key === "Enter") {
      if (field.long && !e.metaKey && !e.ctrlKey) return; // newline inside long text
      e.preventDefault();
      commit("down");
    }
  };

  return (
    <>
      <div className="vde-scroll rounded-xl border border-border bg-card">
        <table className="vde-table" data-testid="vendor-results-table">
          <thead>
            <tr>
              <th className="vde-col-0" scope="col">
                Row
              </th>
              {VENDOR_FIELDS.map((f, i) => (
                <th
                  key={f.key}
                  scope="col"
                  className={cn(i === 0 && "vde-col-1")}
                  style={{ minWidth: f.width, width: f.width }}
                  data-testid={`th-${f.key}`}
                >
                  {f.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, r) => {
              const issues = rowIssues(row);
              return (
                <tr
                  key={row._rid}
                  data-invalid={issues.invalid ? "true" : "false"}
                  data-testid={`vendor-row-${r}`}
                >
                  <td className="vde-col-0">
                    <div className="flex items-center gap-1 px-2 py-2">
                      <span className="vde-code w-4 text-[11px] text-muted-foreground">
                        {r + 1}
                      </span>
                      {issues.invalid ? (
                        <span
                          className="inline-flex items-center gap-0.5 rounded-full bg-danger-surface px-1.5 py-0.5 text-[10px] font-semibold text-danger-text"
                          title={`Invalid: ${issues.errors.join(", ").toUpperCase()}`}
                          data-testid={`row-invalid-badge-${r}`}
                        >
                          <AlertTriangle className="h-2.5 w-2.5" aria-hidden="true" />
                          <span>Invalid</span>
                        </span>
                      ) : null}
                      <button
                        type="button"
                        aria-label={`Delete row ${r + 1}`}
                        title="Delete row"
                        onClick={() => onDeleteRow(row._rid)}
                        data-testid={`delete-row-button-${r}`}
                        className="ml-auto flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-danger-surface hover:text-danger-text"
                      >
                        <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                      </button>
                    </div>
                  </td>

                  {VENDOR_FIELDS.map((f, c) => {
                    const value = row[f.key] ?? "";
                    const st = cellStatus(f.key, value);
                    const isEditing = editing && editing.rid === row._rid && editing.key === f.key;
                    const stateCls =
                      st.level === "error"
                        ? "vde-invalid"
                        : st.level === "warn"
                          ? "vde-warn"
                          : st.level === "needs"
                            ? "vde-needs"
                            : "";
                    return (
                      <td
                        key={f.key}
                        className={cn(c === 0 && "vde-col-1")}
                        style={{ minWidth: f.width, width: f.width }}
                      >
                        {isEditing ? (
                          f.long ? (
                            <textarea
                              ref={editorRef}
                              rows={3}
                              className="vde-cell-input"
                              value={editing.value}
                              onChange={(e) =>
                                setEditing((p) => ({
                                  ...p,
                                  value: normalizeValue(f.key, e.target.value),
                                }))
                              }
                              onBlur={() => commit()}
                              onKeyDown={(e) => onEditorKeyDown(e, f)}
                              data-testid={`cell-input-${f.key}-${r}`}
                              aria-label={`${f.label} row ${r + 1}`}
                            />
                          ) : (
                            <input
                              ref={editorRef}
                              className={cn("vde-cell-input", f.mono && "vde-code")}
                              value={editing.value}
                              onChange={(e) =>
                                setEditing((p) => ({
                                  ...p,
                                  value: normalizeValue(f.key, e.target.value),
                                }))
                              }
                              onBlur={() => commit()}
                              onKeyDown={(e) => onEditorKeyDown(e, f)}
                              data-testid={`cell-input-${f.key}-${r}`}
                              aria-label={`${f.label} row ${r + 1}`}
                            />
                          )
                        ) : (
                          <div
                            id={cellDomId(r, c)}
                            tabIndex={0}
                            role="button"
                            title={st.message || "Click to edit"}
                            aria-label={`${f.label}, row ${r + 1}: ${value || "empty"}${
                              st.message ? `. ${st.message}` : ""
                            }`}
                            className={cn("vde-cell", stateCls, f.mono && "vde-code")}
                            onClick={() => startEdit(r, c)}
                            onKeyDown={(e) => onCellKeyDown(e, r, c)}
                            data-testid={`cell-${f.key}-${r}`}
                          >
                            {st.level === "error" ? (
                              <AlertTriangle
                                className="mr-1 inline h-3 w-3 shrink-0 align-[-1px] text-danger-text"
                                aria-hidden="true"
                              />
                            ) : null}
                            {st.level === "warn" ? (
                              <AlertTriangle
                                className="mr-1 inline h-3 w-3 shrink-0 align-[-1px] text-warning-text"
                                aria-hidden="true"
                              />
                            ) : null}
                            {value ? (
                              f.long ? (
                                <span className="line-clamp-2 inline-block align-top">{value}</span>
                              ) : (
                                value
                              )
                            ) : (
                              <span className="vde-empty">
                                {st.level === "needs" ? "Missing — add it" : "—"}
                              </span>
                            )}
                            {st.level !== "ok" ? (
                              <span className="sr-only">{st.message}</span>
                            ) : null}
                            {f.long && value && value.length > 70 ? (
                              <button
                                type="button"
                                className="mt-1 inline-flex items-center gap-1 text-[10px] font-medium text-primary hover:underline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setExpand({ rid: row._rid, key: f.key, label: f.label, value });
                                }}
                                data-testid={`expand-${f.key}-${r}`}
                              >
                                <Expand className="h-2.5 w-2.5" aria-hidden="true" /> Expand
                              </button>
                            ) : null}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={!!expand} onOpenChange={(o) => !o && setExpand(null)}>
        <DialogContent className="max-w-2xl bg-card" data-testid="expand-dialog">
          <DialogHeader>
            <DialogTitle>{expand?.label}</DialogTitle>
            <DialogDescription>Edit the full text and save it back to the row.</DialogDescription>
          </DialogHeader>
          <Textarea
            className="min-h-[180px] bg-card text-[13px]"
            value={expand?.value || ""}
            onChange={(e) => setExpand((p) => ({ ...p, value: e.target.value }))}
            data-testid="expand-dialog-textarea"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setExpand(null)} data-testid="expand-cancel-button">
              Cancel
            </Button>
            <Button
              onClick={() => {
                onCellChange(expand.rid, expand.key, expand.value);
                setExpand(null);
              }}
              data-testid="expand-save-button"
            >
              Save text
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VendorTable;
